import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatError } from "../api";
import { Shield, Users, CheckCircle2, Trash2, GraduationCap, Download, Upload, Database, RefreshCw, BarChart3, ScrollText, ShieldCheck, FileEdit, Briefcase } from "lucide-react";
import AdminLMS from "./AdminLMS";
import AdminJobsSync from "./AdminJobsSync";
import Analytics from "./Analytics";
import AdminAuditLog from "./AdminAuditLog";
import AdminGovernance from "./AdminGovernance";
import AdminCoeLabs from "./AdminCoeLabs";
import AdminLegalEditor from "./AdminLegalEditor";
import usePageTitle from "../hooks/usePageTitle";
import { KpiTile, SectionCard, DashboardHeader } from "../components/dashboard/shells";

const links = [
  { key: "home",  to: "/admin/dashboard", end: true, label: "Overview", icon: Shield },
  { key: "analytics", to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { key: "governance", to: "/admin/governance", label: "Governance", icon: ShieldCheck },
  { key: "audit", to: "/admin/audit-log", label: "Audit log", icon: ScrollText },
  { key: "users", to: "/admin/users",     label: "All Users", icon: Users },
  { key: "stu",   to: "/admin/users/student",     label: "Students", icon: Users },
  { key: "pro",   to: "/admin/users/professional", label: "Working Pros", icon: Briefcase },
  { key: "co",    to: "/admin/users/company",     label: "Companies", icon: Users },
  { key: "inst",  to: "/admin/users/institute",   label: "Institutes", icon: Users },
  { key: "lms",   to: "/admin/lms", label: "LMS / CoE Labs", icon: GraduationCap },
  { key: "coelabs", to: "/admin/coe-labs", label: "CoE Lab Editor", icon: GraduationCap },
  { key: "jobs",  to: "/admin/jobs-sync", label: "Job Crawler", icon: RefreshCw },
  { key: "data",  to: "/admin/data", label: "Import / Export", icon: Database },
  { key: "legal", to: "/admin/legal", label: "Legal pages", icon: FileEdit },
];

function Overview() {
  usePageTitle("Admin Console");
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then(r => setM(r.data)).catch(() => {}); }, []);
  const loading = m === null;
  return (
    <div className="space-y-4">
      <DashboardHeader
        eyebrow="Command Centre"
        title="Platform overview"
        subtitle={
          m?.pending_students
            ? `${m.pending_students} student approval${m.pending_students === 1 ? "" : "s"} awaiting review.`
            : "All caught up. No pending approvals."
        }
        primary={{ to: "/admin/users/student", label: "Manage students", testid: "admin-cta-users" }}
        secondary={{ to: "/admin/governance", label: "Governance", testid: "admin-cta-gov" }}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiTile icon={Users}         label="Users"          value={m?.users}          accent="#006CFF" testid="stat-users"         loading={loading} to="/admin/users" />
        <KpiTile icon={Users}         label="Students"       value={m?.students}       accent="#0EA5E9" testid="stat-students"      loading={loading} to="/admin/users/student" />
        <KpiTile icon={Briefcase}     label="Working Pros"   value={m?.working_pros}   accent="#9933FF" testid="stat-working-pros"  loading={loading} to="/admin/users/professional" />
        <KpiTile icon={Users}         label="Companies"      value={m?.companies}      accent="#F59E0B" testid="stat-companies"     loading={loading} to="/admin/users/company" />
        <KpiTile icon={Users}         label="Institutes"     value={m?.institutes}     accent="#10B981" testid="stat-institutes"    loading={loading} to="/admin/users/institute" />
        <KpiTile icon={Briefcase}     label="Postings"       value={m?.postings}       accent="#DC2626" testid="stat-postings"      loading={loading} to="/admin/governance" />
        <KpiTile icon={CheckCircle2}  label="Applications"   value={m?.applications}   accent="#0040A8" testid="stat-applications"  loading={loading} />
      </div>

      <SectionCard
        eyebrow="Quick actions"
        title="Get to work"
        testid="admin-quick-actions"
      >
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {[
            { to: "/admin/coe-labs",    label: "CoE Lab Editor",    icon: GraduationCap },
            { to: "/admin/lms",         label: "LMS Curator",       icon: GraduationCap },
            { to: "/admin/jobs-sync",   label: "Job Crawler",       icon: RefreshCw },
            { to: "/admin/audit-log",   label: "Audit Log",         icon: ScrollText },
            { to: "/admin/governance",  label: "Governance",        icon: ShieldCheck },
            { to: "/admin/legal",       label: "Legal Editor",      icon: FileEdit },
            { to: "/admin/data",        label: "Import / Export",   icon: Database },
            { to: "/admin/analytics",   label: "Analytics",         icon: BarChart3 },
          ].map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`admin-qa-${it.to.split("/").pop()}`}
              className="flex items-center gap-2 p-3 rounded-md border border-rule hover:border-signal/40 hover:bg-slate-50/70 transition text-sm font-semibold"
            >
              <it.icon size={14} className="text-signal" />
              {it.label}
            </Link>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function UserTable({ role, subRole }) {
  const [list, setList] = useState([]);
  const [msg, setMsg] = useState("");
  const load = useCallback(
    () => api.get("/admin/users", { params: { role: role || undefined, sub_role: subRole || undefined } }).then(r=>setList(r.data)),
    [role, subRole]
  );
  useEffect(()=>{ load(); }, [load]);
  const approve = async (uid) => { try { await api.post(`/admin/users/${uid}/approve`); setMsg("Approved"); load(); } catch(e){ setMsg(formatError(e)); } };
  const del = async (uid) => { if (!window.confirm("Delete this user?")) return; try { await api.delete(`/admin/users/${uid}`); load(); } catch(e){ setMsg(formatError(e)); } };
  return (
    <>
      <div className="flex justify-between items-center mb-4"><span className="eyebrow">Showing: {role || "all"}{subRole ? ` · ${subRole}` : ""}</span><span className="text-xs text-muted" data-testid="admin-msg">{msg}</span></div>
      <div className="card overflow-x-auto">
        <table className="dense"><thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Approved</th><th>Created</th><th className="text-right">Actions</th></tr></thead>
          <tbody>{list.map(u => (
            <tr key={u.user_id} data-testid={`admin-row-${u.user_id}`}>
              <td>{u.name}</td><td>{u.email}</td>
              <td>
                <span className="badge">{u.role}</span>
                {u.sub_role && (
                  <span className="ml-1 font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`subrole-pill-${u.user_id}`}>
                    {u.sub_role}
                  </span>
                )}
              </td>
              <td>{u.approved ? "Yes" : "No"}</td><td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
              <td className="text-right whitespace-nowrap">
                {!u.approved && <button className="btn-ghost mr-2" onClick={()=>approve(u.user_id)} data-testid={`approve-${u.user_id}`}><CheckCircle2 size={14}/> Approve</button>}
                {u.role !== "admin" && <button className="btn-ghost" onClick={()=>del(u.user_id)} data-testid={`delete-${u.user_id}`}><Trash2 size={14}/> Delete</button>}
              </td>
            </tr>))}
          {list.length===0 && <tr><td colSpan={6} className="text-muted">No users.</td></tr>}</tbody></table>
      </div>
    </>
  );
}

export default function AdminDashboard() {
  return (
    <Layout title="Admin console" links={links}>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="users" element={<UserTable role="" />} />
        <Route path="users/:role" element={<UserRoleWrap />} />
        <Route path="lms" element={<AdminLMS />} />
        <Route path="jobs-sync" element={<AdminJobsSync />} />
        <Route path="data" element={<AdminData />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="audit-log" element={<AdminAuditLog />} />
        <Route path="governance" element={<AdminGovernance />} />
        <Route path="coe-labs" element={<AdminCoeLabs />} />
        <Route path="legal" element={<AdminLegalEditor />} />
        <Route path="*" element={<Overview />} />
      </Routes>
    </Layout>
  );
}

function UserRoleWrap() {
  const role = window.location.pathname.split("/").pop();
  // Pseudo-role "professional" → filter students by sub_role=professional.
  if (role === "professional") return <UserTable role="student" subRole="professional" />;
  return <UserTable role={role} />;
}

function AdminData() {
  const [file, setFile] = useState(null);
  const [role, setRole] = useState("student");
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async (path, filename) => {
    setMsg("");
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setMsg(formatError(e)); }
  };

  const downloadTemplate = () => {
    download(`/admin/bulk-import/template?role=${role}`, `${role}_import_template.csv`);
  };

  const upload = async () => {
    if (!file) { setMsg("Pick a CSV first"); return; }
    setBusy(true); setMsg(""); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("role", role);
    try {
      const r = await api.post("/admin/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) { setMsg(formatError(e)); }
    setBusy(false);
  };

  const ROLE_HINTS = {
    student: "Columns: email, name, phone, password, city, skills, year, branch, affiliated_university",
    company: "Columns: email, name, organization, phone, password, industry, city, website",
    institute: "Columns: email, name, organization, phone, password, institution_type, affiliated_university, city",
  };

  return (
    <div className="space-y-6" data-testid="admin-data-page">
      <div>
        <span className="eyebrow">Bulk operations</span>
        <h2 className="font-display font-bold text-3xl mt-1">Import / Export</h2>
        <p className="text-muted mt-1 max-w-xl">Download CSVs of users and applications, or bulk-create students, working professionals, companies and institutes from a CSV (welcome emails are queued automatically; temporary passwords are returned in the report).</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card card-pad">
          <div className="flex items-center gap-2"><Download size={16} className="text-signal" /><span className="eyebrow">Export</span></div>
          <h3 className="font-display font-bold text-lg mt-2">Download CSVs</h3>
          <p className="text-sm text-muted mt-2">Snapshots for reporting or migration.</p>
          <div className="mt-4 space-y-2">
            <button className="btn-ghost w-full justify-between" onClick={()=>download("/admin/export/users", "users_all.csv")} data-testid="export-users-all">
              All users <Download size={14} />
            </button>
            <button className="btn-ghost w-full justify-between" onClick={()=>download("/admin/export/users?role=student", "users_students.csv")} data-testid="export-users-student">
              Students only <Download size={14} />
            </button>
            <button className="btn-ghost w-full justify-between" onClick={()=>download("/admin/export/users?role=company", "users_companies.csv")} data-testid="export-users-company">
              Companies only <Download size={14} />
            </button>
            <button className="btn-ghost w-full justify-between" onClick={()=>download("/admin/export/applications", "applications.csv")} data-testid="export-applications">
              Applications <Download size={14} />
            </button>
          </div>
        </div>

        <div className="card card-pad">
          <div className="flex items-center gap-2"><Upload size={16} className="text-signal" /><span className="eyebrow">Bulk import</span></div>
          <h3 className="font-display font-bold text-lg mt-2">Onboard from CSV</h3>

          <label className="eyebrow mt-4 block">Role</label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {["student", "working_pro", "company", "institution"].map((r) => (
              <button
                key={r}
                onClick={() => setRole(r)}
                data-testid={`import-role-${r}`}
                className={`text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md border transition-all ${
                  role === r ? "bg-signal text-white border-signal" : "bg-paper text-fg border-rule hover:border-signal"
                }`}
              >
                {r}s
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted mt-3 leading-relaxed">{ROLE_HINTS[role]}</p>

          <button className="btn-ghost mt-3 text-xs" onClick={downloadTemplate} data-testid="import-template-btn">
            <Download size={12} /> Download {role} template
          </button>

          <input type="file" accept=".csv,text/csv" className="mt-4 block text-sm w-full" onChange={(e)=>setFile(e.target.files?.[0] || null)} data-testid="import-file" />
          <button onClick={upload} disabled={busy || !file} className="btn-primary mt-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="import-btn">
            {busy ? "Importing…" : `Import ${role}s`} <Upload size={14} />
          </button>
          {msg && <div className="text-red-600 text-sm mt-3" data-testid="import-msg">{msg}</div>}
          {result && (
            <div className="mt-4 p-3 border border-rule rounded-sm bg-paper text-sm" data-testid="import-result">
              <div className="font-bold text-signal">{result.summary}</div>
              <div className="text-xs text-muted mt-1">Created: {result.created} · Skipped: {result.skipped} · Failed: {result.failed}</div>
              {result.report?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs cursor-pointer text-muted hover:text-fg">View row-by-row report</summary>
                  <ul className="text-[11px] mt-2 max-h-60 overflow-y-auto space-y-0.5 font-mono">
                    {result.report.map((row) => (
                      <li key={`row-${row.row}`} className={
                        row.status === "created" ? "text-emerald-700" :
                        row.status === "skipped" ? "text-amber-700" : "text-red-600"
                      }>
                        Row {row.row} · {row.email} · {row.status}
                        {row.temp_password ? ` · temp pw: ${row.temp_password}` : ""}
                        {row.reason ? ` · ${row.reason}` : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
